import { useEffect, useState } from 'react';
import { Info, X } from 'lucide-react';
import { useMatter } from '../hooks/useMatter';

const STORAGE_KEY = 'adi_portal_no_submission_banner_dismissed_v1';

/**
 * Persistent client-facing reminder that nothing in the portal lodges or
 * submits anything to Home Affairs by itself. Eli's #1 anxiety request from
 * the 5 May meeting; previously clients have worried that filling in fields
 * was tantamount to lodging.
 *
 * Sticky for the session — dismissable but reappears on next sign-in.
 */
export default function NoSubmissionBanner() {
  const { isStaff } = useMatter();
  const [dismissed, setDismissed] = useState<boolean>(() => {
    if (typeof window === 'undefined') return false;
    return sessionStorage.getItem(STORAGE_KEY) === '1';
  });

  useEffect(() => {
    if (dismissed && typeof window !== 'undefined') {
      sessionStorage.setItem(STORAGE_KEY, '1');
    }
  }, [dismissed]);

  if (isStaff || dismissed) return null;

  return (
    <div className="bg-amber-50 border-b border-amber-200 text-amber-900">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-2.5 flex items-start gap-3">
        <Info size={16} className="flex-shrink-0 mt-0.5" aria-hidden="true" />
        <p className="text-sm flex-1">
          <span className="font-semibold">Nothing here is lodged with the Department of Home Affairs.</span>
          {' '}
          You're filling in your information so your case manager can review it. Lodgement
          happens later, only after we confirm everything with you in writing.
        </p>
        <button
          type="button"
          onClick={() => setDismissed(true)}
          className="flex-shrink-0 p-1 rounded hover:bg-amber-100"
          aria-label="Dismiss reminder"
        >
          <X size={14} />
        </button>
      </div>
    </div>
  );
}
